import { createContext, useContext, type ReactNode } from 'react';
import { useToast, type Toast } from '../hooks/useToast';
import { ToastContainer } from '../components/ui/Toast';

interface ToastContextValue {
  toasts: Toast[];
  success: (message: string) => string;
  error: (message: string) => string;
  info: (message: string) => string;
  removeToast: (id: string) => void;
  // Progress toast methods
  progress: (message: string, percent?: number) => string;
  updateProgress: (id: string, percent: number, message?: string) => void;
  completeProgress: (id: string, message?: string) => void;
  failProgress: (id: string, message?: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const {
    toasts,
    success,
    error,
    info,
    removeToast,
    progress,
    updateProgress,
    completeProgress,
    failProgress,
  } = useToast();

  return (
    <ToastContext.Provider
      value={{
        toasts,
        success,
        error,
        info,
        removeToast,
        progress,
        updateProgress,
        completeProgress,
        failProgress,
      }}
    >
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
}

export function useToastContext() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToastContext must be used within a ToastProvider');
  }
  return context;
}
